import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, updateDoc, doc } from 'firebase/firestore';
import type { Organization, AgencyClient, UserProfile } from '@types';
import NewClientModal from './NewClientModal';

interface AgencyDashboardProps {
    currentOrganization: Organization;
    currentUser: UserProfile;
    onNavigate: (nav: { page: any; params?: any }) => void;
    onActivateClient: (client: AgencyClient) => void;
}

const RISK_COLORS: Record<string, string> = {
    Low: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    Medium: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    High: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
    Critical: 'text-red-400 bg-red-500/10 border-red-500/30',
};

const IS_AGENCY_AVAILABLE = false;

const AgencyDashboard: React.FC<AgencyDashboardProps> = ({
    currentOrganization, currentUser, onNavigate, onActivateClient
}) => {
    const [clients, setClients] = useState<AgencyClient[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showNewClientModal, setShowNewClientModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState<'Active' | 'Archived' | 'all'>('Active');

    const slotsUsed = currentOrganization.clientSlotsUsed ?? clients.filter(c => c.status === 'Active').length;
    const slotsTotal = currentOrganization.maxClientSlots ?? 0;
    const slotPct = slotsTotal > 0 ? Math.min((slotsUsed / slotsTotal) * 100, 100) : 0;

    useEffect(() => {
        if (!IS_AGENCY_AVAILABLE || !db || !currentOrganization.id) return;
        setIsLoading(true);

        const q = query(
            collection(db, 'clients'),
            where('organizationId', '==', currentOrganization.id),
            orderBy('createdAt', 'desc')
        );

        const unsub = onSnapshot(q, snap => {
            setClients(snap.docs.map(d => ({ ...d.data(), id: d.id } as AgencyClient)));
            setIsLoading(false);
        }, err => {
            console.error('Clients sync error:', err);
            setIsLoading(false);
        });

        return () => unsub();
    }, [currentOrganization.id]);

    const handleArchiveClient = async (clientId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!db) return;
        try {
            await updateDoc(doc(db, 'clients', clientId), {
                status: 'Archived',
                updatedAt: new Date().toISOString()
            });
        } catch (e) {
            console.error('Archive error:', e);
        }
    };

    const displayedClients = filterStatus === 'all' ? clients : clients.filter(c => (c.status ?? 'Active').toLowerCase() === filterStatus.toLowerCase());

    const isAgency = currentOrganization.tier === 'agency' || (currentOrganization.maxClientSlots ?? 0) > 0;

    // ── Agency tier coming soon — gate for ALL users ─────────────────────────
    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
            <div className="w-24 h-24 bg-slate-800 rounded-3xl flex items-center justify-center mb-8 border border-slate-700">
                <svg className="w-12 h-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0" />
                </svg>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-400 text-[10px] font-black uppercase tracking-widest mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                Coming Soon
            </div>
            <h2 className="text-3xl font-black text-white mb-4 tracking-tight">Agency Business Model</h2>
            <p className="text-slate-400 max-w-lg leading-relaxed mb-3">
                Manage multiple client programmes, apply white-label branding, and deliver scoped intelligence across your entire client roster.
            </p>
            <p className="text-slate-500 text-sm font-bold max-w-md">
                Look out for the future release of our Agency Business Model — coming soon.
            </p>
        </div>
    );

    if (!isAgency) { /* preserved for future release */ }

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Page Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <p className="text-[10px] font-black text-blue-400 uppercase tracking-[0.4em] mb-1">Agency Operations</p>
                    <h1 className="text-3xl font-black text-white">Client Programme Roster</h1>
                    <p className="text-slate-400 mt-1 text-sm">Manage and activate client contexts across your intelligence platform.</p>
                </div>
                <button
                    onClick={() => setShowNewClientModal(true)}
                    disabled={slotsUsed >= slotsTotal}
                    className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-xl font-black uppercase tracking-widest text-xs transition-all shadow-lg shadow-blue-500/20"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                    New Client
                </button>
            </div>

            {/* Capacity & Stats Bar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Slot Meter */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 col-span-1 md:col-span-2">
                    <div className="flex justify-between items-center mb-3">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Client Slot Capacity</span>
                        <span className={`text-xs font-black px-2 py-0.5 rounded border ${slotsUsed >= slotsTotal ? 'text-red-400 bg-red-500/10 border-red-500/30' : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'}`}>
                            {slotsUsed} / {slotsTotal} USED
                        </span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-700 ${slotPct >= 90 ? 'bg-red-500' : slotPct >= 70 ? 'bg-amber-500' : 'bg-blue-500'}`}
                            style={{ width: `${slotPct}%` }}
                        />
                    </div>
                    {slotsUsed >= slotsTotal && (
                        <p className="text-xs text-red-400 mt-2 font-medium">
                            Slot limit reached. <button onClick={() => onNavigate({ page: 'subscriptionManagement' })} className="underline hover:text-red-300 transition-colors">Purchase additional slots →</button>
                        </p>
                    )}
                </div>
                {/* Active Clients Count */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-400 shrink-0">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0" />
                        </svg>
                    </div>
                    <div>
                        <p className="text-3xl font-black text-white">{clients.filter(c => c.status === 'Active').length}</p>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black">Active Clients</p>
                    </div>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800 w-fit">
                {(['Active', 'Archived', 'all'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setFilterStatus(tab)}
                        className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${filterStatus === tab ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'text-slate-400 hover:text-white'}`}
                    >
                        {tab === 'all' ? 'All' : tab}
                    </button>
                ))}
            </div>

            {/* Client List */}
            {isLoading ? (
                <div className="flex items-center justify-center h-48">
                    <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                </div>
            ) : displayedClients.length === 0 ? (
                <div className="bg-slate-900 border-2 border-dashed border-slate-800 rounded-2xl p-12 text-center">
                    <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-600">
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0" />
                        </svg>
                    </div>
                    <p className="text-slate-400 font-medium">No clients found.</p>
                    <p className="text-slate-600 text-sm mt-1">Click "New Client" to onboard your first programme.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {displayedClients.map(client => (
                        <div
                            key={client.id}
                            className="bg-slate-900 border border-slate-800 rounded-2xl p-5 hover:border-blue-500/40 transition-all group relative overflow-hidden cursor-pointer"
                            onClick={() => onNavigate({ page: 'clientDetail', params: { clientId: client.id, clientName: client.name } })}
                        >
                            {/* Hover gradient */}
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

                            <div className="relative z-10">
                                {/* Client Avatar + Name */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center font-black text-blue-400 text-lg border border-blue-500/20">
                                            {client.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <h3 className="font-black text-white text-sm leading-tight group-hover:text-blue-300 transition-colors">{client.name}</h3>
                                            <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">{client.industry || 'General'}</p>
                                        </div>
                                    </div>
                                    <span className={`text-[9px] font-black px-2 py-0.5 rounded border uppercase tracking-widest ${RISK_COLORS[client.riskProfile ?? 'Medium']}`}>
                                        {client.riskProfile}
                                    </span>
                                </div>

                                {/* Country badge */}
                                {(client.operatingCountries?.length ?? 0) > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-4">
                                        {client.operatingCountries!.slice(0, 3).map(c => (
                                            <span key={c} className="text-[9px] font-black text-slate-400 bg-slate-800 px-2 py-0.5 rounded border border-slate-700">{c}</span>
                                        ))}
                                        {(client.operatingCountries!.length > 3) && (
                                            <span className="text-[9px] text-slate-600">+{client.operatingCountries!.length - 3}</span>
                                        )}
                                    </div>
                                )}

                                {/* Action Row */}
                                <div className="flex gap-2 pt-3 border-t border-slate-800">
                                    <button
                                        onClick={e => { e.stopPropagation(); onActivateClient(client); }}
                                        className="flex-1 py-2 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white rounded-lg text-[10px] font-black uppercase tracking-widest transition-all border border-blue-500/30 hover:border-blue-500"
                                    >
                                        Activate Context
                                    </button>
                                    {(client.status ?? 'Active').toLowerCase() !== 'archived' && (
                                        <button
                                            onClick={e => handleArchiveClient(client.id, e)}
                                            className="p-2 bg-slate-800 hover:bg-amber-500/20 text-slate-500 hover:text-amber-400 rounded-lg transition-all border border-slate-700 hover:border-amber-500/30"
                                            title="Archive client (data is preserved)"
                                        >
                                            {/* Archive box icon — NOT a delete/trash action */}
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showNewClientModal && (
                <NewClientModal
                    currentOrganization={currentOrganization}
                    onClose={() => setShowNewClientModal(false)}
                    onSuccess={(client) => { setShowNewClientModal(false); onActivateClient(client); }}
                />
            )}
        </div>
    );
};

export default AgencyDashboard;
